/**
 * Alupave Decking Calculator
 * Reads from window.deckCalcData (pre-filled by Liquid snippet)
 * No external API calls, no hardcoded values — pure metaobject interpreter
 *
 * Key differences from roof calculators:
 * — Shape types replace roof types (square, rectangle, l_shape, j_shape, u_shape)
 * — Stage 2: colour (grey/mill/ral) + direction (up/down/left/right)
 * — Stage 2 image: blank WebP + CSS background for grey/mill; RAL WebP for ral
 * — Dimensions: A, B, C, D — which is width/length axis depends on shape + direction
 * — Board quantity: ceil(widthDimension / boardWidthMm) × splitLength(lengthDimension)
 * — Multi-zone shapes (L, J, U): two zones computed and combined
 * — Dimension expressions in roof_type_overrides JSON e.g. "a-d", "b+c"
 * — resolvedQtys tracks P2 qty for p2_qty_minus_1 formula (P4 connector)
 * — P7 End Cap: sku_fixed_pair_colour_suffix emits LH + RH cart lines
 * — Min size: 1000mm on all active dimension inputs
 * — No max length cap — splitLength handles unlimited runs
 */

(function () {
  'use strict';

  var data = window.deckCalcData;
  if (!data) {
    console.warn('Decking calculator: no data found. Check Liquid snippet.');
    return;
  }

  // ─── State ───────────────────────────────────────────────────────────────
  var state = {
    shapeKey: '',
    shapeName: '',
    colour: '',        // 'grey' | 'mill' | 'ral colour'
    direction: '',     // 'up' | 'down' | 'left' | 'right'
    sizeA: 0,
    sizeB: 0,
    sizeC: 0,
    sizeD: 0,
    cartItems: [],
    resolvedQtys: {}   // keyed by entryLabel — required for p2_qty_minus_1
  };

  // Colour → CSS background colour for blank WebP overlay
  var colourBg = {
    'grey': '#585959',
    'mill': '#C0C0C0'
  };

  // Colour → image suffix
  var colourImageKey = {
    'grey':        'blank',
    'mill':        'blank',
    'ral colour':  'ral'
  };

  // Diagram image base URL — injected from Liquid via asset_url


  // ─── Helpers ─────────────────────────────────────────────────────────────

  /**
   * Evaluate a dimension expression against current state.
   * Supports: 'a', 'b', 'c', 'd', 'a-d', 'b-c', 'a+b', 'b+c', 'a+(2*b)', etc.
   * All letters map to state.sizeA/B/C/D.
   */
  function evalDimension(expr) {
    if (!expr) return 0;
    var resolved = expr.toLowerCase()
      .replace(/\ba\b/g, state.sizeA)
      .replace(/\bb\b/g, state.sizeB)
      .replace(/\bc\b/g, state.sizeC)
      .replace(/\bd\b/g, state.sizeD);
    try {
      // eslint-disable-next-line no-new-func
      return Math.max(0, new Function('return (' + resolved + ');')());
    } catch (e) {
      console.warn('Decking calc: could not evaluate dimension expression "' + expr + '"', e);
      return 0;
    }
  }

  /**
   * Split a measurement across available length tiers.
   * Fewest pieces → fewest unique variants → lowest wastage.
   * Returns array of { variantLabel, quantity }.
   */
  function splitLength(tiers, measurementMm) {
    if (!tiers || tiers.length === 0 || measurementMm <= 0) return [];
    var sorted = tiers.slice().sort(function (a, b) { return a.maxLength - b.maxLength; });

    // Fits within a single tier
    for (var i = 0; i < sorted.length; i++) {
      if (measurementMm <= sorted[i].maxLength) {
        return [{ variantLabel: sorted[i].variantLabel, quantity: 1 }];
      }
    }

    // Fill with multiples of longest, then smallest covering remainder
    var longest = sorted[sorted.length - 1];
    var fullCount = Math.floor(measurementMm / longest.maxLength);
    var remainder = measurementMm % longest.maxLength;
    var results = [];

    if (fullCount > 0) {
      results.push({ variantLabel: longest.variantLabel, quantity: fullCount });
    }
    if (remainder > 0) {
      for (var j = 0; j < sorted.length; j++) {
        if (remainder <= sorted[j].maxLength) {
          results.push({ variantLabel: sorted[j].variantLabel, quantity: 1 });
          break;
        }
      }
    }
    return results;
  }

  // FR locale variant title normalisation
  // Shopify translates variant option values — map FR colour words back to EN for matching
  var colourMap = {
    'gris': 'grey', 'moulin': 'mill', 'couleurral': 'ralcolour'
  };

  function normaliseTitle(title) {
    var t = title.replace(/\s/g, '').replace(/,/g, '.').toLowerCase();
    for (var fr in colourMap) {
      if (colourMap.hasOwnProperty(fr)) {
        t = t.replace(new RegExp(fr, 'g'), colourMap[fr]);
      }
    }
    return t;
  }

  function findVariantByLabel(product, label) {
    if (!product || !product.variants || !label) return null;
    var normLabel = label.replace(/\s/g, '').replace(/,/g, '.').toLowerCase();
    for (var i = 0; i < product.variants.length; i++) {
      var normTitle = normaliseTitle(product.variants[i].title);
      if (normTitle.indexOf(normLabel) !== -1) return product.variants[i];
    }
    return null;
  }

  // Match by colour + length. normaliseTitle() maps FR translated variant values back to EN.
  // When lengthLabel is empty, matches by colour only, falls back to variants[0].
  function findColouredVariant(product, lengthLabel, colour) {
    if (!product || !product.variants) return null;
    var normLen = lengthLabel ? lengthLabel.replace(/\s/g, '').replace(/,/g, '.').toLowerCase() : '';
    var normCol = colour.replace(/\s/g, '').toLowerCase();
    for (var i = 0; i < product.variants.length; i++) {
      var normTitle = normaliseTitle(product.variants[i].title);
      if (normLen === '') {
        if (normTitle.indexOf(normCol) !== -1) return product.variants[i];
      } else {
        if (normTitle.indexOf(normLen) !== -1 && normTitle.indexOf(normCol) !== -1) return product.variants[i];
      }
    }
    if (normLen !== '') return findVariantByLabel(product, lengthLabel);
    return product.variants[0];
  }

  /**
   * Find variant by exact SKU match.
   */
  function findVariantBySku(product, sku) {
    if (!product || !product.variants || !sku) return null;
    var normSku = sku.toUpperCase();
    for (var i = 0; i < product.variants.length; i++) {
      if ((product.variants[i].sku || '').toUpperCase() === normSku) {
        return product.variants[i];
      }
    }
    return null;
  }

  /**
   * Get the roof_type_overrides entry for current shape + direction.
   * Returns the direction-specific sub-object if present, otherwise the shape object.
   */
  function getOverride(rule) {
    var overrides = rule.roofTypeOverrides;
    if (typeof overrides === 'string') {
      try { overrides = JSON.parse(overrides); } catch (e) { return null; }
    }
    if (!overrides) return null;
    var shapeOverride = overrides[state.shapeKey];
    if (!shapeOverride) return null;
    // If direction-keyed, return direction sub-object
    if (state.direction && shapeOverride[state.direction] !== undefined) {
      return shapeOverride[state.direction];
    }
    return shapeOverride;
  }

  /**
   * Get sku_map for a rule, parsing JSON string if needed.
   */
  function getSkuMap(rule) {
    var map = rule.skuMap;
    if (!map) return null;
    if (typeof map === 'string') {
      try { map = JSON.parse(map); } catch (e) { return null; }
    }
    return map;
  }

  /**
   * Resolve colour suffix from skuMap.colourSuffix using current state.colour.
   */
  function getColourSuffix(skuMap) {
    if (!skuMap || !skuMap.colourSuffix) return '';
    return skuMap.colourSuffix[state.colour.toLowerCase()] || '';
  }

  /**
   * Get first product from rule (decking products are single-product rules).
   */
  function getProduct(rule) {
    if (!rule.products || rule.products.length === 0) return null;
    return rule.products[0];
  }

  /**
   * Build the Stage 2/3 diagram image URL for current shape + colour + direction.
   */
  function getDiagramImageUrl(shapeKey, colour, direction) {
    var normShape = shapeKey.replace('decking_', '').replace(/_/g, '-');
    // Grey and Mill both use blank images; RAL uses its own image
    var colourKey = colourImageKey[colour.toLowerCase()] || 'blank';
    var dir = direction || 'up';
    var newFilename = 'alupave-decking-' + normShape + '-' + colourKey + '-' + dir + '.webp';
    // Get base URL from the key attribute on the roofsheet img (set via asset_url in section HTML)
    // Replace just the filename portion (between last / and ?) with the new filename
    var currentUrl = $('.roofsheet img').attr('key') || '';
    if (!currentUrl) return newFilename;
    return currentUrl.replace(/[^/]+\.webp/, newFilename);
  }

  /**
   * Update Stage 2/3 diagram image and background colour.
   */
  function updateDiagramImage() {
    var url = getDiagramImageUrl(state.shapeKey, state.colour, state.direction);
    $('.roofsheet img, .roofsize img, .qimg img').attr('src', url);

    var bg = colourBg[state.colour.toLowerCase()];
    if (bg) {
      $('.roofsheet .diagram-bg, .roofsize .diagram-bg').css('background-color', bg);
    } else {
      // RAL — no background colour needed, image is self-contained
      $('.roofsheet .diagram-bg, .roofsize .diagram-bg').css('background-color', '');
    }
  }

  /**
   * Show/hide C and D dimension inputs based on shape.
   * Square and Rectangle only use A + B.
   * L, J, U shapes use A + B + C + D.
   */
  function updateDimensionInputs() {
    var compoundShapes = ['decking_l_shape', 'decking_j_shape', 'decking_u_shape'];
    var isCompound = compoundShapes.indexOf(state.shapeKey) !== -1;
    if (isCompound) {
      $('.roofinput .sizecd').show();
    } else {
      $('.roofinput .sizecd').hide();
    }
  }


  // ─── Core calculation ────────────────────────────────────────────────────

  function calculateKit() {
    state.cartItems = [];
    state.resolvedQtys = {};

    var rules = data.productRules;

    for (var r = 0; r < rules.length; r++) {
      var rule = rules[r];
      var override = getOverride(rule);
      var product = getProduct(rule);
      var variantDrivenBy = rule.variantDrivenBy;
      var formula = rule.qtyFormula;
      var skuMap = getSkuMap(rule);

      if (!product) continue;

      // ── decking_board — multi-zone, width multiplier × splitLength ────────
      if (variantDrivenBy === 'decking_board') {
        if (!override) continue;

        var zones = [];

        if (override.zone1_width !== undefined) {
          zones.push({
            widthExpr:  override.zone1_width,
            lengthExpr: override.zone1_length
          });
        }
        if (override.zone2_width !== undefined) {
          zones.push({
            widthExpr:  override.zone2_width,
            lengthExpr: override.zone2_length
          });
        }

        var totalBoards = {};  // variantLabel → total qty across zones

        for (var z = 0; z < zones.length; z++) {
          var widthMm  = evalDimension(zones[z].widthExpr);
          var lengthMm = evalDimension(zones[z].lengthExpr);
          if (widthMm <= 0 || lengthMm <= 0) continue;

          var widthMultiplier = Math.ceil(widthMm / data.boardWidthMm);
          var splits = splitLength(rule.lengthTiers, lengthMm);

          for (var s = 0; s < splits.length; s++) {
            var label = splits[s].variantLabel;
            var qty   = splits[s].quantity * widthMultiplier;
            totalBoards[label] = (totalBoards[label] || 0) + qty;
          }
        }

        // Emit one cart line per variant label
        var boardQtyTotal = 0;
        for (var lbl in totalBoards) {
          if (!totalBoards.hasOwnProperty(lbl)) continue;
          var boardVariant = findColouredVariant(product, lbl, state.colour);
          if (boardVariant) {
            var boardQty = totalBoards[lbl];
            boardQtyTotal += boardQty;
            state.cartItems.push({
              rule: rule,
              product: product,
              variant: boardVariant,
              quantity: boardQty,
              editable: true
            });
          }
        }
        state.resolvedQtys[rule.entryLabel] = boardQtyTotal;
        continue;
      }

      // ── split_length — single dimension expression from override ──────────
      if (variantDrivenBy === 'split_length') {
        if (!override) continue;
        // override is the dimension expression string e.g. "a", "a+(2*b)"
        var splitExpr = (typeof override === 'string') ? override : null;
        if (!splitExpr) continue;

        var splitMm = evalDimension(splitExpr);
        if (splitMm <= 0) continue;

        var splitResults = splitLength(rule.lengthTiers, splitMm);
        var splitQtyTotal = 0;
        for (var sr = 0; sr < splitResults.length; sr++) {
          var splitVariant = findColouredVariant(product, splitResults[sr].variantLabel, state.colour);
          if (splitVariant) {
            splitQtyTotal += splitResults[sr].quantity;
            state.cartItems.push({
              rule: rule,
              product: product,
              variant: splitVariant,
              quantity: splitResults[sr].quantity,
              editable: true
            });
          }
        }
        state.resolvedQtys[rule.entryLabel] = splitQtyTotal;
        continue;
      }

      // ── fixed — fixed qty from override or qty_multiplier_default ─────────
      if (variantDrivenBy === 'fixed') {
        var fixedQty = 0;

        if (formula === 'p2_qty_minus_1') {
          // P4 Connector: P2 (Gutter) total pieces minus 1
          var gutterLabel = null;
          for (var gr = 0; gr < rules.length; gr++) {
            if (rules[gr].entryLabel && rules[gr].entryLabel.indexOf('Gutter') !== -1
                && rules[gr].variantDrivenBy === 'split_length') {
              gutterLabel = rules[gr].entryLabel;
              break;
            }
          }
          var gutterQty = gutterLabel ? (state.resolvedQtys[gutterLabel] || 0) : 0;
          fixedQty = Math.max(0, gutterQty - 1);

        } else if (formula === 'fixed') {
          fixedQty = override ? (override.fixed_qty || rule.qtyMultiplierDefault || 0)
                              : (rule.qtyMultiplierDefault || 0);
        }

        if (fixedQty <= 0) {
          state.resolvedQtys[rule.entryLabel] = 0;
          continue;
        }

        var fixedVariant = rule.fixedVariantLabel
          ? findVariantByLabel(product, rule.fixedVariantLabel)
          : product.variants[0];

        if (fixedVariant) {
          state.cartItems.push({
            rule: rule,
            product: product,
            variant: fixedVariant,
            quantity: fixedQty,
            editable: true
          });
        }
        state.resolvedQtys[rule.entryLabel] = fixedQty;
        continue;
      }

      // ── sku_fixed_pair_colour_suffix — P7 End Cap: LH + RH cart lines ─────
      if (variantDrivenBy === 'sku_fixed_pair_colour_suffix') {
        if (!skuMap) continue;

        var colourSuffix = getColourSuffix(skuMap);
        var pairQtys = override || {};  // { lh: N, rh: N }
        var lhQty = pairQtys.lh || 0;
        var rhQty = pairQtys.rh || 0;

        if (skuMap.LH && lhQty > 0) {
          var lhSku = skuMap.LH + colourSuffix;
          var lhVariant = findVariantBySku(product, lhSku);
          if (lhVariant) {
            state.cartItems.push({
              rule: rule,
              product: product,
              variant: lhVariant,
              quantity: lhQty,
              editable: true
            });
          }
        }

        if (skuMap.RH && rhQty > 0) {
          var rhSku = skuMap.RH + colourSuffix;
          var rhVariant = findVariantBySku(product, rhSku);
          if (rhVariant) {
            state.cartItems.push({
              rule: rule,
              product: product,
              variant: rhVariant,
              quantity: rhQty,
              editable: true
            });
          }
        }

        state.resolvedQtys[rule.entryLabel] = lhQty + rhQty;
        continue;
      }
    }

    renderKitList();
  }


  // ─── Render kit list ─────────────────────────────────────────────────────

  function renderKitList() {
    var container  = document.querySelector('.dkit .kit-list');
    var mcontainer = document.querySelector('.mkit .kit-list');
    if (!container) {
      console.warn('Decking calculator: .kit-list container not found');
      return;
    }

    var html  = '';
    var mhtml = '';
    var total         = 0;
    var totalSaved    = 0;

    for (var i = 0; i < state.cartItems.length; i++) {
      var item       = state.cartItems[i];
      var price      = item.variant.price / 100;
      var compareAt  = item.variant.compareAtPrice ? item.variant.compareAtPrice / 100 : null;
      var lineTotal  = price * item.quantity;
      var lineSaving = compareAt && compareAt > price ? (compareAt - price) * item.quantity : 0;
      total       += lineTotal;
      totalSaved  += lineSaving;

      var image = item.variant.image || (item.product ? item.product.featuredImage : '') || '';

      // Desktop row
      html += '<div class="kit-item" data-index="' + i + '">';
      html += '  <div class="kit-item-image">' + (image ? '<img src="' + image + '" alt="">' : '') + '</div>';
      html += '  <div class="kit-item-details">';
      html += '    <div class="kit-item-title">' + (item.product ? item.product.title : item.rule.entryLabel) + '</div>';
      html += '    <div class="kit-item-variant">' + (item.variant.title !== 'Default Title' ? item.variant.title : '') + '</div>';
      html += '    <div class="kit-item-compare">';
      if (compareAt && compareAt > price) {
        html += '<span class="kit-price-sale">' + formatMoney(price) + '</span>';
        html += '<span class="kit-price-compare">' + formatMoney(compareAt) + '</span>';
      } else {
        html += '<span>' + formatMoney(price) + '</span>';
      }
      html += '  </div></div>';
      html += '  <div class="kit-item-qty">';
      html += '    <button class="kit-qty-minus" data-index="' + i + '">−</button>';
      html += '    <input type="number" class="kit-qty-input" data-index="' + i + '" value="' + item.quantity + '" min="0">';
      html += '    <button class="kit-qty-plus" data-index="' + i + '">+</button>';
      html += '  </div>';
      html += '  <div class="kit-item-price">' + formatMoney(lineTotal) + '</div>';
      html += '</div>';

      // Mobile row
      mhtml += '<div class="kit-item" data-index="' + i + '">';
      mhtml += '  <div class="w-100"><div class="w-25">';
      mhtml += '    <div class="kit-item-image">' + (image ? '<img src="' + image + '" alt="">' : '') + '</div>';
      mhtml += '  </div><div class="w-75">';
      mhtml += '    <div class="kit-item-details">';
      mhtml += '      <div class="kit-item-title">' + (item.product ? item.product.title : item.rule.entryLabel) + '</div>';
      mhtml += '      <div class="kit-item-variant">' + (item.variant.title !== 'Default Title' ? item.variant.title : '') + '</div>';
      mhtml += '      <div class="kit-item-compare">';
      if (compareAt && compareAt > price) {
        mhtml += '<span class="kit-price-sale">' + formatMoney(price) + '</span>';
        mhtml += '<span class="kit-price-compare">' + formatMoney(compareAt) + '</span>';
      } else {
        mhtml += '<span>' + formatMoney(price) + '</span>';
      }
      mhtml += '    </div></div>';
      mhtml += '    <div class="kit-item-qty">';
      mhtml += '      <button class="kit-qty-minus" data-index="' + i + '">−</button>';
      mhtml += '      <input type="number" class="kit-qty-input" data-index="' + i + '" value="' + item.quantity + '" min="0">';
      mhtml += '      <button class="kit-qty-plus" data-index="' + i + '">+</button>';
      mhtml += '    </div>';
      mhtml += '  </div></div>';
      mhtml += '</div>';
    }

    container.innerHTML  = html;
    mcontainer.innerHTML = mhtml;

    $('.kitcart .total span').html(formatMoney(total));

    if (totalSaved > 0) {
      var ysav = 'You saved ';
      if (window.location.pathname.indexOf('fr-fr') === 1) { ysav = 'Vous avez enregistré '; }
      $('.kitcart .saved').show().html(ysav + formatMoney(totalSaved) + '!');
    } else {
      $('.kitcart .saved').hide();
    }

    showStep('kit');
    bindKitListEvents();
  }


  // ─── Kit list events ─────────────────────────────────────────────────────

  function bindKitListEvents() {
    document.querySelectorAll('.kit-qty-minus').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var idx = parseInt(this.getAttribute('data-index'));
        if (state.cartItems[idx].quantity > 0) {
          state.cartItems[idx].quantity--;
          renderKitList();
        }
      });
    });

    document.querySelectorAll('.kit-qty-plus').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var idx = parseInt(this.getAttribute('data-index'));
        state.cartItems[idx].quantity++;
        renderKitList();
      });
    });

    document.querySelectorAll('.kit-qty-input').forEach(function (input) {
      input.addEventListener('change', function () {
        var idx = parseInt(this.getAttribute('data-index'));
        var val = parseInt(this.value) || 0;
        if (val < 0) val = 0;
        state.cartItems[idx].quantity = val;
        renderKitList();
      });
    });

    document.querySelectorAll('.kit-add-to-cart').forEach(function (btn) {
      btn.addEventListener('click', function () {
        document.querySelectorAll('.kit-add-to-cart').forEach(function (b) {
          b.setAttribute('disabled', 'disabled');
          b.textContent = 'Adding to cart...';
        });
        addToCart();
      });
    });
  }


  // ─── Cart submission ─────────────────────────────────────────────────────

  function addToCart() {
    var items = [];

    for (var i = 0; i < state.cartItems.length; i++) {
      var item = state.cartItems[i];
      if (item.quantity < 1) continue;
      items.push({
        quantity: item.quantity,
        id: item.variant.id,
        properties: {
          '_DECK SHAPE':   state.shapeName,
          '_COLOUR':       state.colour,
          '_DIRECTION':    state.direction,
          '_LENGTH A':     state.sizeA,
          '_LENGTH B':     state.sizeB,
          '_LENGTH C':     state.sizeC || '',
          '_LENGTH D':     state.sizeD || ''
        }
      });
    }

    if (items.length === 0) {
      alert('No items to add. Please check your quantities.');
      document.querySelectorAll('.kit-add-to-cart').forEach(function (btn) {
        btn.removeAttribute('disabled');
        btn.textContent = 'Add full kit to cart';
      });
      return;
    }

    items.reverse();

    jQuery.post('/cart/add.js', { items: items }).then(function () {
      window.location.href = data.cartUrl || '/cart';
    }).fail(function () {
      alert('There was an error adding to cart. Please try again.');
      document.querySelectorAll('.kit-add-to-cart').forEach(function (btn) {
        btn.removeAttribute('disabled');
        btn.textContent = 'Add full kit to cart';
      });
    });
  }


  // ─── Currency formatting ─────────────────────────────────────────────────

  function formatMoney(amount) {
    var currency = data.currency || {};
    var locale   = currency.locale || 'en-GB';
    var code     = currency.code   || 'GBP';
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency: code }).format(amount);
    } catch (e) {
      return (currency.symbol || '£') + amount.toFixed(2);
    }
  }


  // ─── Step management ─────────────────────────────────────────────────────

  function setSteps(step) {
    $('.rooftype, .roofsheet, .roofsize, .kit-list-wrapper').hide();
    $(".roofcal .number").removeClass("current complete");
    if (step === 1) {
      $('.rooftype').show();
      $(".roofcal .number.wtor").addClass("current");
    } else if (step === 2) {
      $('.roofsheet').show();
      $(".roofcal .number.wtor").addClass("complete");
      $(".roofcal .number.yrc").addClass("current");
    } else if (step === 3) {
      $('.roofsize').show();
      $(".roofcal .number.wtor, .roofcal .number.yrc").addClass("complete");
      $(".roofcal .number.cyrs").addClass("current");
    }
  }


  // ─── Step management ─────────────────────────────────────────────────────

  function showStep(step) {
    $('.rooftype, .roofsheet, .roofsize, .kit-list-wrapper').hide();
    var stepMap = { shapetype: 'wtos', colour: 'yrc', size: 'cyds', kit: 'kit' };
    if (step === 'shapetype')  { $('.rooftype').show(); }
    else if (step === 'colour')     { $('.roofsheet').show(); }
    else if (step === 'size')       { $('.roofsize').show(); }
    else if (step === 'kit')        { $('.kit-list-wrapper').show(); }
    updateStepIndicators(step);
  }

  function updateStepIndicators(currentStep) {
    $('.roofcal .number').removeClass('current complete');
    var steps = ['wtos', 'yrc', 'cyds', 'kit'];
    var stepKeys = ['shapetype', 'colour', 'size', 'kit'];
    var currentIdx = stepKeys.indexOf(currentStep);
    for (var i = 0; i < steps.length; i++) {
      if (i < currentIdx)       { $('.roofcal .number.' + steps[i]).addClass('complete'); }
      else if (i === currentIdx) { $('.roofcal .number.' + steps[i]).addClass('current'); }
    }
  }


  // ─── Event bindings ──────────────────────────────────────────────────────

  // Step 1 — Shape selection
  $('.roofcal .roofimg').click(function () {
    var shapeIndex = parseInt($(this).attr('key')) - 1;
    var shapeType  = data.shapeTypes[shapeIndex];
    if (!shapeType) return;

    state.shapeKey  = shapeType.key;
    state.shapeName = shapeType.name;
    state.colour    = '';
    state.direction = '';

    // Reset colour + direction selects
    $('select[name="colour1"]').val('');
    $('select[name="direction"]').val('');

    // Set Stage 2/3 diagram image to selected shape (blank/up as default)
    // key attribute provides the resolved CDN base URL for getDiagramImageUrl
    var defaultImg = getDiagramImageUrl(shapeType.key, 'grey', 'up');
    $('.roofsheet img, .roofsize img').attr('src', defaultImg).attr('key', defaultImg);
    console.log(shapeIndex, shapeType.key);
    if(shapeType.key == 'decking_rectangle' || shapeType.key == 'decking_square'){ $(".extra, .sizec, .sized").hide(); }else{ $(".extra, .sizec, .sized").show(); }

    showStep('colour');
    setSteps(2);
  });

  // Back to Step 1
  $('.srtype').click(function () {
    state.shapeKey  = '';
    state.shapeName = '';
    showStep('shapetype');
  });

  // Step 2 — Colour selection
  $('select[name="colour1"]').change(function () {
    state.colour = $(this).val().toLowerCase();
    if (state.direction) updateDiagramImage();
    var colour2 = '#58595b';
    if (state.colour == 'grey') { colour2 = '#58595b'; }
    if (state.colour == 'mill') { colour2 = '#bcbec0'; }
    if (state.colour == 'sand') { colour2 = '#D2A869'; }
    if (state.colour == 'ral colour') { colour2 = '#5a3826'; }
    $('.clr').text(state.color);
    $('.roofsheet img, .roofsize img, .qimg img').css('background',colour2);
  });
  $('select[name="colour1"]').change(function () {
      state.colour1 = $(this).val();
      var text = this.options[this.selectedIndex].text;
      $('.quote .clr').text(text);
      updateDiagramImage();
    });
  // Step 2 — Direction selection
  $('select[name="direction"]').change(function () {
    state.direction = $(this).val().toLowerCase();
    if (state.colour) updateDiagramImage();
  });

  // Back to Step 2
  $(".yrc").click(function () {
    console.log("Step 2")
    if (!state.roofKey) return false;
    showSteps('colour');
  });

  // Step 2 → Step 3
  $('.rfsize').click(function () {
    if (!state.shapeKey) {
      alert('Please select a Deck Shape');
      return false;
    }
    if (!state.colour) {
      alert('Please select a Colour');
      return false;
    }
    if (!state.direction) {
      alert('Please select a Direction');
      return false;
    }
    updateDimensionInputs();
    showStep('size');
    setSteps(3);
  });

  // Back to Step 2 from Step 3
  $('.roofcal .yrc').click(function () {
    if (!state.shapeKey) return false;
    showStep('colour');
  });

  // Step 3 — Calculate kit
  $('.ckit').click(function () {
    $(this).attr('disabled', 'disabled');

    state.sizeA = parseInt($('.roofinput input[name="lengtha"]').val()) || 0;
    state.sizeB = parseInt($('.roofinput input[name="lengthb"]').val()) || 0;
    state.sizeC = parseInt($('.roofinput input[name="lengthc"]').val()) || 0;
    state.sizeD = parseInt($('.roofinput input[name="lengthd"]').val()) || 0;

    $('.sizea span').text(state.sizeA);
    $('.sizeb span').text(state.sizeB);
    $('.sizec span').text(state.sizeC);
    $('.sized span').text(state.sizeD);
    console.log(state.sizeA), state.sizeB;

    // Determine which dimensions are active for this shape
    var compoundShapes = ['decking_l_shape', 'decking_j_shape', 'decking_u_shape'];
    var isCompound = compoundShapes.indexOf(state.shapeKey) !== -1;

    // Validate — all active inputs must be >= 1000mm
    var invalid = false;
    if (state.sizeA < 1000 || state.sizeB < 1000) { invalid = true; }
    if (isCompound && (state.sizeC < 1000 || state.sizeD < 1000)) { invalid = true; }

    if (invalid) {
      alert(data.minSizeMessage || 'Please enter a minimum size of 1000mm for all dimensions');
      $(this).attr('disabled', false);
      return false;
    }

    calculateKit();
    $(this).attr('disabled', false);

    $(".roofcal .number").removeClass("current complete");
    $(".roofcal .number.wtor, .roofcal .number.yrc, .roofcal .number.cyrs").addClass("complete");
    $(".roofcal .number.kit").addClass("current");
  });

  // Re-enable calculate button when any size input changes
  $('.roofinput input').change(function () {
    $('.ckit').attr('disabled', false);
  });

  // Back to Step 3 from kit list
  $('.kit-back-btn').click(function () {
    showStep('size');
  });

})();